/**
 * Контракты между слоями. Реализации живут в apps/api, но интерфейсы и
 * DTO лежат здесь, чтобы фронтенд и бэкенд не разъезжались.
 */

import type {
  Car,
  GasStation,
  LatLon,
  Plan,
  PlannerSettings,
  RawCar,
  RouteLeg,
  TravelMode,
} from './types.js';

// --- Провайдеры ------------------------------------------------------------

/** Шаг авторизации: CityDrive использует телефон + код из SMS. */
export interface LoginStartResult {
  /** Токен-черновик, который надо вернуть вместе с кодом из SMS. */
  challengeId: string;
  /** Через сколько секунд можно запросить код повторно. */
  resendAfterS?: number;
  /** Ожидаемая длина кода, чтобы форма подстроилась. */
  codeLength?: number;
}

export interface CityDriveSession {
  accessToken: string;
  refreshToken?: string;
  expiresAtISO?: string;
  userId?: string;
  phone?: string;
}

export interface CityDriveClient {
  /** Запрашивает SMS-код на номер. */
  startLogin(phone: string): Promise<LoginStartResult>;
  /** Меняет код из SMS на сессию. */
  confirmLogin(
    challengeId: string,
    code: string,
    phone: string,
  ): Promise<CityDriveSession>;
  /** Обновляет протухший токен; бросает, если не поддерживается. */
  refresh(session: CityDriveSession): Promise<CityDriveSession>;
  /** Машины вокруг точки. Радиус в километрах. */
  listCars(
    session: CityDriveSession,
    center: LatLon,
    radiusKm: number,
  ): Promise<RawCar[]>;
  /** Жив ли токен. */
  isSessionValid(session: CityDriveSession): Promise<boolean>;
}

export interface GasStationProvider {
  findStations(center: LatLon, radiusKm: number): Promise<GasStation[]>;
}

export interface RouteRequest {
  from: LatLon;
  to: LatLon;
  mode: TravelMode;
}

export interface RouteResult {
  distanceM: number;
  durationS: number;
  geometry?: LatLon[];
}

export interface RoutingProvider {
  /** Точный маршрут между двумя точками. */
  route(request: RouteRequest): Promise<RouteResult>;
  /** Пакетный расчёт, чтобы не бить по одному запросу на ногу. */
  routeMany(requests: readonly RouteRequest[]): Promise<Array<RouteResult | null>>;
  /** Доступен ли точный роутинг (есть ли ключ). */
  readonly precise: boolean;
}

// --- HTTP API: пути --------------------------------------------------------

export const API_ROUTES = {
  health: '/api/health',
  loginStart: '/api/auth/login/start',
  loginConfirm: '/api/auth/login/confirm',
  logout: '/api/auth/logout',
  me: '/api/auth/me',
  settings: '/api/settings',
  cars: '/api/cars',
  plan: '/api/plan',
  planById: '/api/plan/:id',
  planActive: '/api/plan/active',
  planComplete: '/api/plan/:id/stop/:index/complete',
  planSkip: '/api/plan/:id/stop/:index/skip',
  events: '/api/events',
  config: '/api/config',
} as const;

// --- HTTP API: DTO ---------------------------------------------------------

export interface LoginStartRequest {
  phone: string;
}

export interface LoginConfirmRequest {
  challengeId: string;
  code: string;
  phone: string;
}

export interface MeResponse {
  authenticated: boolean;
  phone?: string;
  /** Работает ли сервис на моковых данных вместо живого CityDrive. */
  mock: boolean;
}

export interface CarsRequest {
  lat: number;
  lon: number;
  radiusKm?: number;
}

export interface CarsResponse {
  cars: Car[];
  rejected: Array<{ car: Car; reason: string }>;
  fetchedAtISO: string;
  /** Данные из мок-адаптера. */
  mock: boolean;
}

export interface PlanRequest {
  start: LatLon;
  settings?: Partial<PlannerSettings>;
  /** Не уточнять ноги через 2GIS — быстрее, но времена приблизительные. */
  skipPreciseRouting?: boolean;
}

export interface PlanResponse {
  plan: Plan;
  stations: GasStation[];
  mock: boolean;
}

export interface ConfigResponse {
  /** Ключ MapGL для фронтенда. */
  mapglKey: string | null;
  mock: boolean;
  monitorIntervalS: number;
  defaultSettings: PlannerSettings;
}

export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
}

/** Утилита: ноги плана в плоский список для пакетного роутинга. */
export function collectLegs(plan: Plan): Array<{
  stopIndex: number;
  legIndex: number;
  leg: RouteLeg;
}> {
  const out: Array<{ stopIndex: number; legIndex: number; leg: RouteLeg }> = [];
  for (const stop of plan.stops) {
    for (const [legIndex, leg] of stop.legs.entries()) {
      if (leg.kind !== 'refuel') {
        out.push({ stopIndex: stop.index, legIndex, leg });
      }
    }
  }
  return out;
}
